/**
 * Message types for cross-origin communication between parent and iframe
 */
export const MessageTypes = {
  // Lifecycle
  INIT: 'init',
  READY: 'ready',
  DESTROY: 'destroy',
  PING: 'ping',
  PONG: 'pong',
  
  // UI Control
  SHOW_WIDGET: 'show-widget',
  HIDE_WIDGET: 'hide-widget',
  TOGGLE_WIDGET: 'toggle-widget',
  MINIMIZE_WIDGET: 'minimize-widget',
  MAXIMIZE_WIDGET: 'maximize-widget',
  
  // Widget State Communication (ChatInterface → Orchestrator)
  WIDGET_STATE_CHANGE: 'widget-state-change',
  
  // Configuration
  UPDATE_CONFIG: 'update-config',
  UPDATE_LAYOUT: 'update-layout',
  GET_CONFIG: 'get-config',
  CONFIG_UPDATED: 'config-updated',
  
  // Events from ChatInterface
  MESSAGE_SENT: 'message-sent',
  MESSAGE_RECEIVED: 'message-received',
  CONVERSATION_STARTED: 'conversation-started',
  CONVERSATION_ENDED: 'conversation-ended',
  TYPING_START: 'typing-start',
  TYPING_STOP: 'typing-stop',
  CONNECTION_STATUS: 'connection-status',
  ERROR: 'error',
  
  // State changes
  UNREAD_COUNT_CHANGE: 'unread-count-change',
  
  // Resize and positioning  
  PARENT_RESIZE: 'parent-resize',
  
  // Message injection
  INJECT_USER_MESSAGE: 'inject-user-message',
  INJECT_AGENT_RESPONSE: 'inject-agent-response',
  CLEAR_CHAT: 'clear-chat',
  
  // Iframe initialization flow
  OVERLAY_CONFIG_LOADED: 'overlay-config-loaded',
  IFRAME_READY_TO_DISPLAY: 'iframe-ready-to-display',
  CHAT_INTERFACE_READY: 'chat-interface-ready',

  // Side panel
  PANEL_OPENED: 'panel-opened',
  PANEL_CLOSED: 'panel-closed',
  PANEL_TOGGLE: 'panel-toggle',

  // Full screen resizable window
  FULLSCREEN_EXPANDED: 'fullscreen-expanded',
  FULLSCREEN_COLLAPSED: 'fullscreen-collapsed',
  FULLSCREEN_RESIZE: 'fullscreen-resize',

  // Component expand/collapse (expand A2UI components beyond iframe)
  COMPONENT_EXPAND: 'component-expand',
  COMPONENT_COLLAPSE: 'component-collapse'
} as const;

/**
 * Message sources for validation
 */
export const MessageSources = {
  ORCHESTRATOR: 'kaigentic-orchestrator',
  CHAT_IFRAME: 'kaigentic-chat-iframe',
  PARENT: 'kaigentic-parent',
  UNKNOWN: 'unknown'
} as const;

export type MessageTypesType = typeof MessageTypes[keyof typeof MessageTypes];
export type MessageSourcesType = typeof MessageSources[keyof typeof MessageSources];

/**
 * Base message interface
 */
export interface BaseMessage {
  source: MessageSourcesType;
  type: MessageTypesType;
  timestamp: number;
  requestId?: string;
}

/**
 * Message with data payload
 */
export interface DataMessage<T = any> extends BaseMessage {
  data: T;
}

/**
 * Response message for API calls
 */
export interface ResponseMessage<T = any> extends BaseMessage {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * API call messages are no longer supported
 */

/**
 * Configuration update message
 */
export interface ConfigUpdateMessage extends BaseMessage {
  type: typeof MessageTypes.UPDATE_CONFIG | typeof MessageTypes.UPDATE_LAYOUT;
  data: any;
}

/**
 * State change messages are no longer supported - use WIDGET_STATE_CHANGE instead
 */

/**
 * Chat event message
 */
export interface ChatEventMessage extends BaseMessage {
  type: typeof MessageTypes.MESSAGE_SENT | typeof MessageTypes.MESSAGE_RECEIVED | typeof MessageTypes.CONVERSATION_STARTED;
  data: {
    message?: any;
    conversation?: any;
    metadata?: any;
  };
}

/**
 * Error message
 */
export interface ErrorMessage extends BaseMessage {
  type: typeof MessageTypes.ERROR;
  data: {
    message: string;
    code?: string;
    stack?: string;
    context?: any;
  };
}

/**
 * Resize message
 */
export interface ResizeMessage extends BaseMessage {
  type: typeof MessageTypes.PARENT_RESIZE;
  data: {
    width: number;
    height: number;
    viewport?: {
      width: number;
      height: number;
    };
  };
}

/**
 * Widget state for responsive floating behavior
 */
export type WidgetState = 'launcher' | 'expanded' | 'minimized' | 'fullscreen';

/**
 * Widget dimensions with responsive breakpoints
 */
export interface WidgetDimensions {
  width: number | string;
  height: number | string;
  position?: {
    top?: string | number;
    left?: string | number;
    right?: string | number;
    bottom?: string | number;
  };
  zIndex?: number;
  borderRadius?: number;
}

/**
 * Widget state change message (ChatInterface → Orchestrator)
 */
export interface WidgetStateMessage extends BaseMessage {
  type: typeof MessageTypes.WIDGET_STATE_CHANGE;
  data: {
    state: WidgetState;
    dimensions: WidgetDimensions;
    animated?: boolean;
    position?: string; // 'bottom-right', 'bottom-left', etc.
    viewport?: {
      width: number;
      height: number;
      breakpoint: 'mobile' | 'tablet' | 'desktop';
    };
  };
}

/**
 * Widget interaction messages (now handled via WIDGET_STATE_CHANGE)
 * These interfaces are kept for backward compatibility but are deprecated
 */

/**
 * Message injection messages
 */
export interface InjectionMessage extends BaseMessage {
  type: typeof MessageTypes.INJECT_USER_MESSAGE | typeof MessageTypes.INJECT_AGENT_RESPONSE | typeof MessageTypes.CLEAR_CHAT;
  data: {
    content?: string | object | (string | object)[];
    message?: string;
  };
}

/**
 * Overlay config loaded message (ChatInterface → Orchestrator)
 */
export interface OverlayConfigLoadedMessage extends BaseMessage {
  type: typeof MessageTypes.OVERLAY_CONFIG_LOADED;
  data: {
    config: any; // Will be OverlayConfig type from ChatInterface
    source: 'backend' | 'cache' | 'default';
    overlayName: string;
  };
}

/**
 * Iframe ready to display message (Orchestrator → ChatInterface)
 */
export interface IframeReadyToDisplayMessage extends BaseMessage {
  type: typeof MessageTypes.IFRAME_READY_TO_DISPLAY;
  data: {
    finalConfig?: any; // Optional merged config
  };
}

/**
 * Chat interface ready message (ChatInterface → Orchestrator)
 */
export interface ChatInterfaceReadyMessage extends BaseMessage {
  type: typeof MessageTypes.CHAT_INTERFACE_READY;
  data: {
    timestamp: number;
  };
}

/**
 * Side panel state change message (ChatInterface → Orchestrator)
 */
export interface PanelStateMessage extends BaseMessage {
  type: typeof MessageTypes.PANEL_OPENED | typeof MessageTypes.PANEL_CLOSED;
  data: {
    panelWidth: number;
    panelPosition: 'left' | 'right';
    totalWidth: number; // chat width + panel width
    totalHeight: number;
  };
}

/**
 * Union type for all message types
 */
export type OrchestratorMessage =
  | BaseMessage
  | DataMessage
  | ResponseMessage
  | ConfigUpdateMessage
  | ChatEventMessage
  | ErrorMessage
  | ResizeMessage
  | WidgetStateMessage
  | InjectionMessage
  | OverlayConfigLoadedMessage
  | IframeReadyToDisplayMessage
  | ChatInterfaceReadyMessage
  | PanelStateMessage;

/**
 * Message handler function type
 */
export type MessageHandler<T extends OrchestratorMessage = OrchestratorMessage> = (message: T) => void | Promise<void>;

/**
 * Message validation result
 */
export interface MessageValidationResult {
  isValid: boolean;
  source: MessageSourcesType;
  type: MessageTypesType;
  error?: string;
  silent?: boolean; // Don't log warnings for browser extension messages
  isEventBridge?: boolean; // Indicates message needs EventBridge transformation
}

/**
 * Message queue item for handling async responses
 */
export interface QueuedMessage {
  message: OrchestratorMessage;
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timeout: number;
  timestamp: number;
}